import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AsciiPanel,
  AsciiButton,
  AsciiInput,
} from "@/components/ui/ascii-theme";

const LoginPage: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const endpoint = isLogin ? "/api/auth/login" : "/api/auth/register";
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Important for cookies
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Authentication failed");
        setLoading(false);
        return;
      }

      // Success - redirect to home
      navigate("/");
    } catch (err) {
      setError("Network error. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-black font-mono flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* ASCII Art Header */}
        <pre className="text-xs sm:text-sm mb-8 whitespace-pre overflow-x-auto">
          {`
╔═══════════════════════════════════════════════════════════════════╗
║                                                                   ║
║   ███╗   ███╗ █████╗ ██╗  ██╗███╗   ███╗ █████╗ ██╗ ██████╗       ║
║   ████╗ ████║██╔══██╗╚██╗██╔╝████╗ ████║██╔══██╗██║██╔═══██╗      ║
║   ██╔████╔██║███████║ ╚███╔╝ ██╔████╔██║███████║██║██║   ██║      ║
║   ██║╚██╔╝██║██╔══██║ ██╔██╗ ██║╚██╔╝██║██╔══██║██║██║   ██║      ║
║   ██║ ╚═╝ ██║██║  ██║██╔╝ ██╗██║ ╚═╝ ██║██║  ██║██║╚██████╔╝      ║
║   ╚═╝     ╚═╝╚═╝  ╚═╝╚═╝  ╚═╝╚═╝     ╚═╝╚═╝  ╚═╝╚═╝ ╚═════╝       ║
║                                                                   ║
╚═══════════════════════════════════════════════════════════════════╝
`}
        </pre>

        {/* Auth Box */}
        <AsciiPanel className="bg-white">
          <div className="mb-6">
            <div className="text-center text-xl mb-4 font-bold">
              [ {isLogin ? "SYSTEM LOGIN" : "USER REGISTRATION"} ]
            </div>
            <div className="text-center text-xs opacity-60">
              {isLogin
                ? "> Enter credentials to access system_"
                : "> Initialize new user account_"}
            </div>
          </div>

          {error && (
            <div className="mb-4 border-2 border-red-600 bg-red-50 p-3">
              <span className="text-red-700 font-bold">[ERROR] {error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <AsciiInput
                type="email"
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white"
                placeholder="user@domain.com"
                required
                disabled={loading}
              />
            </div>

            <div>
              <AsciiInput
                type="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white"
                placeholder="••••••••"
                required
                disabled={loading}
              />
              {!isLogin && (
                <div className="text-xs opacity-60 mt-1">
                  * Minimum 6 characters required
                </div>
              )}
            </div>

            <AsciiButton
              type="submit"
              disabled={loading}
              variant="primary"
              size="md"
              className="w-full justify-center py-3"
            >
              {loading ? "Processing..." : isLogin ? "Login" : "Register"}
            </AsciiButton>
          </form>

          <div className="mt-6 text-center border-t-2 border-black pt-6">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError("");
              }}
              className="text-xs sm:text-sm opacity-60 hover:opacity-100 transition-colors font-medium cursor-pointer"
              disabled={loading}
            >
              {isLogin ? (
                <span>
                  &gt; Need an account? Accounts reserved for Max and friends
                  [REGISTER]
                </span>
              ) : (
                <span>&gt; Have an account? [LOGIN]</span>
              )}
            </button>
          </div>
        </AsciiPanel>

        {/* Footer */}
        <div className="mt-4 text-center text-xs opacity-60">
          <div>System Access Control v1.0</div>
          <div className="mt-1">Session duration: 365 days</div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
